import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppProviders } from './app-providers';
import './i18n';
import { usingMocks } from './lib/api/http';
import { router } from './routes/router';
import './styles/index.css';

/**
 * Application entry point.
 *
 * The one interesting line is the MSW start. In dev, `VITE_USE_MSW` defaults to *on*, so a fresh
 * clone runs the whole console — all nine screens, ~200 seeded transactions, working state
 * transitions — with no backend at all. Setting it to `false` points the same code at the real
 * PaymentFlow operator API and the real mock-website BFF through the dev-server proxy. No
 * component knows which it is talking to; that is the point of building against contract-derived
 * mocks.
 *
 * The literal `import.meta.env.DEV` in the guard is load-bearing: Vite replaces it with `false`
 * in any production build, so the whole branch — including the dynamic import — is eliminated
 * and the `mocks/` chunk is never emitted. `usingMocks()` alone is not enough here, because
 * Rollup does not fold constants across a function call, and an un-dropped dynamic import would
 * ship the entire mock backend to production (which is exactly what happened on 2026-08-05).
 */
async function start(): Promise<void> {
  if (import.meta.env.DEV && usingMocks()) {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass',
      quiet: true,
      serviceWorker: { url: `${import.meta.env.BASE_URL}mockServiceWorker.js` },
    });
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

void start();
